import { withErrorBoundary } from "@client/components/utils/ErrorBoundary";
import { Tab, Tabs } from "@client/components/utils/Tabs";
import { StateContext } from "@client/pages/round/Round";
import {
  getActiveProposals,
  isHold,
  isUserRoundAdminOrFunder,
} from "@engine/round/accessors";
import { Evaluator } from "@lib/types";
import { assertUnreachable } from "@lib/utils/assertUnreachable";
import { useContext, useState } from "react";
import { MVFTable } from "../MVFTable";
import { SidebarTab } from "./EvaluatorSidebar/EvaluatorSidebar";
import { IncrementalEvals } from "./IncrementalEvals/IncrementalEvals";
import { MatchingTable } from "./MatchingTable";
import { type Domains, MVFGraph } from "./MVFGraph/MVFGraph";
import { RatingTable } from "./RatingTable/RatingTable";
import { TrancheListEditor } from "./TrancheListEditor/TrancheListEditor";
import { FrontendEvaluation, MVFUpdate } from "./useEvaluatorEvaluationsCache";

export type EvaluatorTab =
  | "mvf-graph"
  | "tranche-editor"
  | "incremental-evals"
  | "rating-table";

export function evaluatorTabOf(view: string | undefined): EvaluatorTab {
  switch (view) {
    case "tranche-editor":
    case "incremental-evals":
    case "rating-table":
      return view;
    default:
      return "mvf-graph";
  }
}

export const EvaluatorView = withErrorBoundary(
  ({
    evaluations,
    userCanUpdateEvaluations,
    mvfUpdate,
    mvfCommit,
    unilateralAllocations,
    evaluator,
    roundId,
    node,
    view,
    showRightSidebar,
    domains,
    setDomains,
  }: {
    evaluator: Evaluator;
    evaluations: FrontendEvaluation[];
    userCanUpdateEvaluations: boolean;
    mvfUpdate: MVFUpdate;
    mvfCommit: () => void;
    unilateralAllocations: {
      [evaluableId: string]: {
        amount: number;
      };
    };
    roundId: string;
    node: string;
    view?: EvaluatorTab;
    showRightSidebar: boolean;
    domains: Domains;
    setDomains: (domains: Domains) => void;
  }) => {
    const { round, actingAsUserId: userId } = useContext(StateContext);
    const userIsRoundAdminOrFunder = isUserRoundAdminOrFunder(round, userId);
    const [sidebarTab, setSidebarTab] = useState<
      "mvf-table" | "matching-table"
    >("mvf-table");

    const isEvaluatorJt = ["Jaan Tallinn", "JT"].includes(evaluator.shortname);
    const showTrancheEditorTab = false;
    // !( evaluator.type === "recommender" && evaluator.meanRecommenderThreshold);

    let tabs = [
      <Tab
        key="mvf-graph"
        selected={view === "mvf-graph" || view === undefined}
        href={`~/round/${roundId}/allocations/${node}/mvf-graph`}
      >
        MVF Graph
      </Tab>,
    ];

    if (showTrancheEditorTab) {
      tabs.push(
        <Tab
          key="tranche-editor"
          selected={view === "tranche-editor"}
          href={`~/round/${roundId}/allocations/${node}/tranche-editor`}
        >
          Tranche List Editor
        </Tab>,
      );
    }

    if (
      evaluator.type === "funder" &&
      isEvaluatorJt &&
      userIsRoundAdminOrFunder
    ) {
      tabs.push(
        <Tab
          key="incremental-evals"
          selected={view === "incremental-evals"}
          href={`~/round/${roundId}/allocations/${node}/incremental-evals`}
        >
          Incremental Evals
        </Tab>,
        <Tab
          key="rating-table"
          selected={view === "rating-table"}
          href={`~/round/${roundId}/allocations/${node}/rating-table`}
        >
          Rating Table
        </Tab>,
      );
    }

    const activeProposals = getActiveProposals(round);
    const matchingProposals = activeProposals.filter(
      (p) => p.requestsMatchingPledge && !!p.typedFields.matchRate,
    );
    const matchingProposalIds = new Set(matchingProposals.map((p) => p.id));
    const matchingEvaluations = evaluations.filter((e) =>
      matchingProposalIds.has(e.evaluableId),
    );

    const tabView = (function () {
      // @ts-ignore
      switch (view) {
        case "mvf-graph":
        case undefined:
          return (
            <div className="flex flex-1 pt-4 mr-4 overflow-y-hidden relative">
              <MVFGraph
                evaluations={evaluations}
                userCanEditEvaluations={
                  userCanUpdateEvaluations && !isEvaluatorJt
                }
                mvfUpdate={mvfUpdate}
                mvfCommit={mvfCommit}
                unilateralAllocations={unilateralAllocations}
                domains={domains}
                setDomains={setDomains}
              />
              {showRightSidebar && (
                <div className="flex flex-col">
                  <div className="flex border-b-blue-400 bg-stone-200 dark:bg-inherit">
                    <SidebarTab
                      label="Matching Table"
                      isActive={sidebarTab === "matching-table"}
                      onClick={() => setSidebarTab("matching-table")}
                    />
                    <SidebarTab
                      label="MVF Table"
                      isActive={sidebarTab === "mvf-table"}
                      onClick={() => setSidebarTab("mvf-table")}
                    />
                  </div>
                  <div className="overflow-auto">
                    {sidebarTab === "matching-table" ? (
                      (() => {
                        return (
                          <MatchingTable
                            proposals={matchingProposals}
                            evaluations={matchingEvaluations}
                            evaluator={evaluator}
                          />
                        );
                      })()
                    ) : (
                      <MVFTable
                        evaluations={evaluations.filter(
                          (e) => !isHold(round, e.evaluable),
                        )}
                        evaluator={evaluator}
                      />
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        case "tranche-editor":
          return (
            <TrancheListEditor
              evaluator={evaluator}
              unilateralAllocations={unilateralAllocations}
            />
          );
        case "incremental-evals":
          if (evaluator.type !== "funder") {
            return <div>Only funders</div>;
          }
          return <IncrementalEvals funder={evaluator} />;
        case "rating-table":
          if (evaluator.type !== "funder") {
            return <div>Only funders</div>;
          }
          return <RatingTable funder={evaluator} />;
        default:
          // @ts-ignore
          assertUnreachable(view);
      }
    })();

    return (
      <>
        {tabs.length > 1 && <Tabs>{tabs}</Tabs>}
        {tabView}
      </>
    );
  },
);
